import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { playersAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import { getErrorMessage } from '../../utils/errorMessage';
import type { Player, UpdatePlayerContactRequest } from '../../types/player';
import { BackHeader, LoadingRows, SectionLabel } from '../../components/family/ui';
import AbsencePanel from '../../components/players/AbsencePanel';
import PlayerContactForm from '../../components/players/PlayerContactForm';

const card = 'rounded-2xl bg-white p-4 ring-1 ring-black/5';

/** Egy gyerek (vagy a játékos saját) adatlapja: hiányzás bejelentése, elérhetőség, egészség. */
const FamilyPlayerPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { showToast } = useToast();
  const [player, setPlayer] = useState<Player | null>(null);
  const [missing, setMissing] = useState(false);
  const [saving, setSaving] = useState(false);

  usePageTitle(player?.name ?? 'Adatlap');

  useEffect(() => {
    // Másik gyerekre váltáskor az előző adatlap ne maradjon kint (különben a
    // "nem található" üzenet alatt a korábbi gyerek űrlapja látszott, mentéssel)
    let cancelled = false;
    setPlayer(null);
    setMissing(false);
    playersAPI
      .getMyPlayers()
      .then((mine) => {
        if (cancelled) return;
        const found = mine.find((p) => p.id === Number(id));
        if (found) setPlayer(found);
        else setMissing(true);
      })
      .catch(() => {
        if (!cancelled) setMissing(true);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleSave = async (data: UpdatePlayerContactRequest) => {
    if (!player) return;
    setSaving(true);
    try {
      setPlayer(await playersAPI.updateContact(player.id, data));
      showToast('Adatok elmentve.');
    } catch (err) {
      showToast(getErrorMessage(err, 'A mentés nem sikerült.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <BackHeader backLabel="Profil" back="/me" />

      {missing && <p className={`${card} text-[15px] text-gray-600`}>Ez a játékos nem található.</p>}
      {!player && !missing && <LoadingRows rows={2} />}

      {player && (
        <>
          <h1 className="text-[26px] font-bold leading-tight text-gray-900">{player.name}</h1>
          <p className="mt-0.5 text-[15px] text-gray-500">{player.ageGroup}</p>

          <SectionLabel>Beteg vagy sérült?</SectionLabel>
          <div className={card}>
            <AbsencePanel playerId={player.id} playerName={player.name} startOpen={searchParams.get('absence') === '1'} />
          </div>

          <SectionLabel>Elérhetőség és egészség</SectionLabel>
          <div className={card}>
            <PlayerContactForm player={player} saving={saving} onSubmit={handleSave} />
            {player.contactUpdatedAt && (
              <p className="mt-2 text-[13px] text-gray-400">
                Utoljára frissítve: {new Date(player.contactUpdatedAt).toLocaleDateString('hu-HU')}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default FamilyPlayerPage;
